import clamp from "lodash/clamp";

import {
  MedicalReviewState,
  OrderedOnboardingSteps,
} from "@/constants/Onboarding";
import useOnboardingSplashScreenVisibility from "@/hooks/onboarding/useOnboardingSplashScreenVisibility";
import { useAppSelector } from "@/store/hooks";
import {
  selectOnboardingCurrentStepIndex,
  selectOnboardingIsMedicalReviewCleared,
  selectOnboardingMedicalReviewState,
  useOnboardingDispatch,
} from "@/store/reducers/onboarding";
import { Button, FAIconName, IconButton } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

const LowerLimit = 0;
const UpperLimit = OrderedOnboardingSteps.length;
const baseTestID = "onboarding-dev-controls";

// Matches the prototype's holdAt=2: gate fires after Profile + Clinical Picture
const MedicalReviewGateThreshold = 2;

// Forward: pending -> approved -> notApproved -> cleared
const ReviewStateForwardCycle: Record<
  MedicalReviewState,
  MedicalReviewState | undefined
> = {
  [MedicalReviewState.Approved]: MedicalReviewState.NotApproved,
  [MedicalReviewState.NotApproved]: undefined,
  [MedicalReviewState.Pending]: MedicalReviewState.Approved,
};

// Reverse: notApproved -> approved -> pending -> (decrement step)
const ReviewStateReverseCycle: Record<
  MedicalReviewState,
  MedicalReviewState | undefined
> = {
  [MedicalReviewState.Approved]: MedicalReviewState.Pending,
  [MedicalReviewState.NotApproved]: MedicalReviewState.Approved,
  [MedicalReviewState.Pending]: undefined,
};

// TODO: Remove (JN-3043) -- not ideal, but onboarding has a lot of one-shot flows
// that are painful to reach manually. This gives devs and QA a way to jump between
// steps and replay the splash without restarting. Safe to ship -- onboarding v2 is
// behind a LaunchDarkly feature flag with a forced version upgrade before enabling.

const OnboardingDevControls = () => {
  const currentStepIndex = useAppSelector(selectOnboardingCurrentStepIndex);
  const isMedicalReviewCleared = useAppSelector(
    selectOnboardingIsMedicalReviewCleared,
  );
  const medicalReviewState = useAppSelector(selectOnboardingMedicalReviewState);
  const {
    setCurrentStepIndex,
    setMedicalReviewCleared,
    setMedicalReviewState,
  } = useOnboardingDispatch();
  const { resetSplash } = useOnboardingSplashScreenVisibility();

  const isMedicalReviewVisible =
    currentStepIndex >= MedicalReviewGateThreshold && !isMedicalReviewCleared;

  const handleNext = () => {
    if (isMedicalReviewVisible) {
      const currentReview = medicalReviewState ?? MedicalReviewState.Pending;
      const nextReview = ReviewStateForwardCycle[currentReview];

      if (nextReview) {
        setMedicalReviewState(nextReview);
      } else {
        setMedicalReviewCleared();
      }

      return;
    }

    setCurrentStepIndex(clamp(currentStepIndex + 1, LowerLimit, UpperLimit));
  };

  const handlePrevious = () => {
    // Re-enter the review gate when stepping back from a cleared state
    if (
      currentStepIndex >= MedicalReviewGateThreshold &&
      isMedicalReviewCleared
    ) {
      setMedicalReviewState(MedicalReviewState.NotApproved);
      return;
    }

    if (isMedicalReviewVisible) {
      const currentReview = medicalReviewState ?? MedicalReviewState.Pending;
      const prevReview = ReviewStateReverseCycle[currentReview];

      if (prevReview) {
        setMedicalReviewState(prevReview);
      } else {
        setCurrentStepIndex(currentStepIndex - 1);
      }

      return;
    }

    setCurrentStepIndex(clamp(currentStepIndex - 1, LowerLimit, UpperLimit));
  };

  return (
    <VStack alignItems="center" gap={12} justifyContent="center">
      <HStack gap={12} paddingTop={16}>
        <IconButton
          iconName={FAIconName.ArrowLeft}
          isDisabled={currentStepIndex === LowerLimit}
          onPress={handlePrevious}
          testID={joinTestIDs(baseTestID, "prev")}
        />
        <IconButton
          iconName={FAIconName.ArrowRight}
          isDisabled={currentStepIndex >= UpperLimit}
          onPress={handleNext}
          testID={joinTestIDs(baseTestID, "next")}
        />
      </HStack>
      <Button
        onPress={resetSplash}
        testID={joinTestIDs(baseTestID, "reset-splash")}
        title="Reset splash"
        variant="tertiary"
      />
    </VStack>
  );
};

export { MedicalReviewGateThreshold, OnboardingDevControls };
